import {useMemo} from 'react'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'
import type {useSubIssuesSummary$key} from './__generated__/useSubIssuesSummary.graphql'
import type {SubIssueSummaryData} from '@github-ui/issue-create/Model'

/**
 * `subIssuesSummary` is updated asynchronously, meaning that it is not guarunteed to be synchronized with `subIssues`.
 * This can cause issues when the sub-issues list is visible, as `subIssuesSummary` may not align with the list of
 * visible issues. To resolve this, we calculate the summary on the client side if the sub-issues list is loaded.
 *
 * Consumers should *always* prefer calling `useSubIssuesSummary` over reading the `subIssuesSummary` field directly.
 *
 * Note: this would work much better as a [derived field](https://relay.dev/docs/guides/relay-resolvers/derived-fields/)
 * but that's an experimental v17 Relay feature we don't have access to yet.
 */
export function useSubIssuesSummary(issueKey?: useSubIssuesSummary$key): SubIssueSummaryData {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment useSubIssuesSummary on Issue @argumentDefinitions(fetchSubIssues: {type: "Boolean!"}) {
        subIssuesSummary @skip(if: $fetchSubIssues) {
          total
          completed
        }
        subIssuesConnection: subIssues @include(if: $fetchSubIssues) {
          # Important to use totalCount (not subIssuesSummary.total) so we use the same field everywhere, keeping in sync when mutating
          totalCount
        }
        subIssues(first: 100) @include(if: $fetchSubIssues) {
          nodes {
            closed
          }
        }
      }
    `,
    issueKey,
  )

  const {subIssuesSummary, subIssues, subIssuesConnection} = data || {}
  const total = subIssues?.nodes?.length ?? subIssuesConnection?.totalCount ?? subIssuesSummary?.total ?? 0
  const completed = subIssues?.nodes?.filter(issue => issue?.closed).length ?? subIssuesSummary?.completed ?? 0

  return useMemo(
    () => ({
      total,
      completed,
      percentCompleted: total !== 0 ? Math.floor((completed / total) * 100) : 0,
    }),
    [total, completed],
  )
}
